import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { useSEO } from '@/hooks/useSEO';

const categoryColors: Record<string, string> = {
  '維修案例': 'bg-blue-100 text-blue-600',
  '優惠活動': 'bg-orange-100 text-orange-600',
  '公司動態': 'bg-green-100 text-green-600',
  '維修教學': 'bg-purple-100 text-purple-600',
};

const categories = ['全部', '維修案例', '優惠活動', '公司動態', '維修教學'];

interface NewsArticle {
  id: string;
  title: string;
  content: string;
  summary: string;
  category: string;
  image: string;
  published_at: string;
  published: boolean;
}

export default function NewsPage() {
  useSEO({
    title: '最新消息｜維修案例・優惠活動・維修教學',
    description: '兄弟維修最新消息，包含 iPhone、iPad、MacBook 真實維修案例分享、優惠活動公告、維修教學文章。了解最新維修資訊，掌握裝置保養技巧。',
    keywords: '手機維修案例,iPhone維修案例,MacBook維修案例,維修優惠,維修教學',
    canonical: '/news',
  });

  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('全部');
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const { data, error } = await supabase
        .from('news_articles')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false });

      if (error) throw error;
      setArticles(data || []);
    } catch (err) {
      console.error('Failed to fetch news:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = activeCategory === '全部' ? articles : articles.filter((n) => n.category === activeCategory);

  return (
    <>
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-32 pb-12 bg-gray-950">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-4">
              <span className="w-8 h-px bg-orange-400" />
              最新消息
              <span className="w-8 h-px bg-orange-400" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-4">消息與維修案例</h1>
            <p className="text-gray-400 max-w-xl mx-auto">
              最新維修案例分享、優惠活動、公司動態及維修教學，讓您的裝置使用更放心
            </p>
          </div>
        </section>

        {/* Filter */}
        <section className="bg-white border-b border-gray-100 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 py-4 overflow-x-auto">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
              <span className="text-gray-400 text-sm ml-auto whitespace-nowrap">{filtered.length} 篇文章</span>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-gray-400">目前沒有此分類的文章</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((article) => (
                  <div
                    key={article.id}
                    onClick={() => navigate(`/news/${article.id}`)}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 right-3">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${categoryColors[article.category] ?? 'bg-gray-100 text-gray-600'}`}>
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-gray-400 text-xs mb-3">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-calendar-line" />
                        </div>
                        {article.published_at}
                      </div>
                      <h2 className="font-bold text-gray-900 text-base leading-snug mb-2 line-clamp-2">{article.title}</h2>
                      <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">{article.summary}</p>
                      <div className="flex items-center gap-1 text-orange-500 text-sm font-medium">
                        閱讀更多
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}